/* components/patterns/RecurringConsentModal.jsx — последний шаг перед оплатой
   российской картой: условия автопродления + ОБЯЗАТЕЛЬНАЯ галочка согласия.

   Зачем отдельный шаг. Первая оплата картой привязывает её, и дальше тариф
   продлевается списанием без участия человека. Раньше об этом на шаге оплаты не
   говорилось ничего: согласие было только косвенным (галочка «принимаю оферту»
   при регистрации). Банк в споре «операцию не совершал» спрашивает страницу, где
   плательщик согласился именно на автосписания, — это она. Сервер без
   `recurring_consent: true` такой платёж не создаёт и записывает момент согласия
   в Payment.meta.

   Один компонент на три входа (тариф в настройках, мастер бизнес-пространства,
   счёт по ссылке): текст условий — то, что предъявляют банку, и копии разошлись
   бы именно в нём. Текст — lib/recurringConsentText.js, правило «когда
   спрашивать» — lib/recurringConsent.js.

   Оферта открывается ВНУТРИ модалки (как в InvoicePayModal): правила платёжных
   систем требуют, чтобы галочка стояла на той же странице, что и условия, без
   ухода плательщика на другую.

   Пропсы:
     planName, priceRub, periodDays — тариф, цена ТАРИФА (её спишет продление) и срок;
     payNowRub — сумма первого платежа, если известна (счёт по ссылке);
     prorated — апгрейд: первый платёж — цена нового тарифа минус остаток текущего,
                точную сумму знает только сервер;
     guestPays — платит не владелец аккаунта (счёт по ссылке): настроек у него нет;
     startsOn — режим «смена тарифа» (понижение, 17.10.2026): сейчас не платят, первое
                списание по цене нового тарифа — с УЖЕ привязанной карты в этот день
                (ISO-дата окончания текущего тарифа); свой текст условий и своя кнопка;
     notice — пояснение над условиями (окно открыто заново: сервер ответил, что цена
              тарифа изменилась, — priceChangedNotice в recurringConsentText.js);
     onConfirm() — галочка стоит, человек нажал «Перейти к оплате» / «Запланировать переход». */

import React from 'react';
import { fmt } from '../../lib/format.js';
import { consentTerms, periodWords, planChangeTerms } from '../../lib/recurringConsentText.js';
import { DemoLegalDoc } from '../../screens/demo/DemoLegalDoc.jsx';
import { Button } from '../ui/Button.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Modal } from '../ui/Modal.jsx';

  const { useEffect, useState } = React;

  const known = (v) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;

  function Row({ label, value }) {
    return (
      <div className="checkout-row">
        <span className="checkout-row__label">{label}</span>
        <span className="checkout-row__value">{value}</span>
      </div>
    );
  }

  function RecurringConsentModal({ open, planName, priceRub, periodDays, payNowRub,
                                   prorated = false, guestPays = false, startsOn = null,
                                   notice = null, busy = false, onConfirm, onClose }) {
    const [accepted, setAccepted] = useState(false);
    const [showOffer, setShowOffer] = useState(false);   // оферта открыта внутри модалки
    const change = !!startsOn;                            // смена тарифа, а не оплата сейчас
    const startsText = change ? fmt.date(startsOn) : '';

    // Галочка не переживает закрытие и смену тарифа: согласие даётся на
    // конкретную оплату, а не «один раз на всё».
    useEffect(() => {
      if (open) { setAccepted(false); setShowOffer(false); }
    }, [open, planName, priceRub, periodDays, startsOn]);

    // Апгрейд: сервер списывает цену нового тарифа за вычетом неиспользованного
    // остатка текущего (billing.calculate_proration) и начинает период заново.
    // Точную сумму знает только он — здесь её не выдумываем.
    const payNow = known(payNowRub)
      ? fmt.money(Number(payNowRub))
      : (prorated
        ? 'Сумму покажет страница оплаты'
        : (known(priceRub) ? fmt.money(Number(priceRub)) : null));
    const next = known(priceRub)
      ? fmt.money(Number(priceRub)) + ' каждые ' + periodWords(periodDays)
      : 'Стоимость тарифа каждые ' + periodWords(periodDays);

    const confirm = () => { if (accepted && !busy && onConfirm) onConfirm(); };

    return (
      <Modal open={!!open}
        onClose={busy ? undefined : (showOffer ? () => setShowOffer(false) : onClose)}
        title={showOffer ? 'Пользовательское соглашение' : (change ? 'Смена тарифа' : 'Оплата картой')}
        size={showOffer ? 'lg' : 'sm'} labelledBy="recurring-consent-title"
        footer={showOffer
          ? <Button variant="ghost" iconLeft="arrow-left" onClick={() => setShowOffer(false)}>
              {change ? 'Назад' : 'Назад к оплате'}
            </Button>
          : (
            <>
              <Button variant="ghost" disabled={busy} onClick={onClose}>Отмена</Button>
              <Button variant="primary" iconLeft={change ? 'check' : 'card'} loading={busy}
                disabled={!accepted || busy} data-testid="recurring-consent-pay" onClick={confirm}>
                {change ? 'Запланировать переход' : 'Перейти к оплате'}
              </Button>
            </>
          )}>
        {showOffer ? (
          <DemoLegalDoc kind="offer" />
        ) : (
          <div className="stack stack-16" data-testid="recurring-consent">
            {notice && (
              <div className="callout callout--warn" role="status"
                data-testid="recurring-consent-notice">
                <Icon name="alert-triangle" size={16} />
                <div className="grow t-body-sm">{notice}</div>
              </div>
            )}
            {change ? (
              <div className="checkout-details">
                {planName && <Row label="Новый тариф" value={planName} />}
                <Row label="Сейчас" value="Ничего не списываем" />
                <Row label="Первое списание"
                  value={startsText + (known(priceRub) ? ' — ' + fmt.money(Number(priceRub)) : '')} />
                <Row label="Далее автоматически" value={next} />
              </div>
            ) : (
              <div className="checkout-details">
                {planName && <Row label="Тариф" value={planName} />}
                {payNow && <Row label="К оплате сейчас" value={payNow} />}
                <Row label="Далее автоматически" value={next} />
              </div>
            )}

            {prorated && !change && !known(payNowRub) && (
              <p className="t-body-sm text-dim">
                Сейчас спишется цена нового тарифа за вычетом неиспользованного остатка
                текущего. Оплаченный период начнётся заново с сегодняшнего дня.
              </p>
            )}

            <p className="t-body-sm text-dim" data-testid="recurring-consent-terms">
              {change
                ? planChangeTerms({ priceRub, periodDays, startsOn: startsText })
                : consentTerms({ priceRub, periodDays, guestPays })}
            </p>

            {/* Та же разметка согласия, что на регистрации и в счёте для юрлиц:
                бокс и «Я согласен» тогглят чекбокс, ссылка на оферту — отдельная
                кнопка вне <label>, чтобы клик по ней не ставил галочку. */}
            <div className="auth-form__agree">
              <input id="recurring-consent-agree" type="checkbox" className="checkbox__input"
                checked={accepted} disabled={busy}
                aria-label={'Я согласен на автоматические списания с '
                  + (change ? 'привязанной' : 'этой') + ' карты и принимаю условия оферты'}
                onChange={(e) => setAccepted(e.target.checked)} />
              <label htmlFor="recurring-consent-agree" className="checkbox__box" aria-hidden="true">
                <Icon name="check" size={12} />
              </label>
              <span className="auth-form__agree-text">
                <label htmlFor="recurring-consent-agree" className="auth-form__agree-lead">
                  Я согласен на автоматические списания с {change ? 'привязанной' : 'этой'} карты
                  {' '}и принимаю условия
                </label>{' '}
                <button type="button" className="demo-linkbtn" onClick={() => setShowOffer(true)}>оферты</button>
              </span>
            </div>
          </div>
        )}
      </Modal>
    );
  }

export { RecurringConsentModal };
